import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Lock } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppProvider } from "@/context/AppContext";
import Layout from "@/components/Layout";
import SimulatorBar from "@/components/SimulatorBar";
import Login from "@/pages/Login";
import AccountSetup from "@/pages/AccountSetup";
import ForcePasswordChange from "@/pages/ForcePasswordChange";
import Account from "@/pages/Account";
import AdminUsers from "@/pages/admin/Users";
import AdminOverview from "@/pages/admin/Overview";
import BrokerSession from "@/pages/BrokerSession";
import ConnectZerodha from "@/pages/ConnectZerodha";
import Dashboard from "@/pages/Dashboard";
import LegacyDashboard from "@/pages/LegacyDashboard";
import Explorer from "@/pages/Explorer";
import Opportunities from "@/pages/Opportunities";
import PaperTrading from "@/pages/PaperTrading";
import ActiveTrades from "@/pages/ActiveTrades";
import PaperBook from "@/pages/PaperBook";
import StrategyLibrary from "@/pages/StrategyLibrary";
import StrategyBuilder from "@/pages/StrategyBuilder";
import ActiveStrategies from "@/pages/ActiveStrategies";
import StrategyDetail from "@/pages/StrategyDetail";
import RiskCentre from "@/pages/RiskCentre";
import ShadowLive from "@/pages/ShadowLive";
import TradeJournal from "@/pages/TradeJournal";
import OptionsScanner from "@/pages/options/OptionsScanner";
import OptionChain from "@/pages/options/OptionChain";
import OptionsValidation from "@/pages/options/OptionsValidation";
import OptionsSettings from "@/pages/options/OptionsSettings";
import OptionsHistory from "@/pages/options/OptionsHistory";
import OptionsHedge from "@/pages/options/OptionsHedge";
import OptionsDiagnostics from "@/pages/options/OptionsDiagnostics";
import IronCondor from "@/pages/options/IronCondor";
import CommonTradePanel from "@/pages/CommonTradePanel";
import SettingsPage from "@/pages/SettingsPage";
import SystemHealth from "@/pages/SystemHealth";
import ModuleGate, { FeatureUnavailable, ModuleNotActivated } from "@/components/ModuleGate";
import { MODULE } from "@/lib/modules";
import { IRON_CONDOR_ENABLED, OPTIONS_ENABLED, OPTIONS_HEDGE_ENABLED } from "@/lib/optionsApi";
import { useProducts, allows as allowsProduct, FUTURES, OPTIONS } from "@/lib/products";
import { P } from "@/lib/permissions";
import { useTheme } from "@/context/ThemeContext";


function NotPermitted({ permission }) {
  const trading = permission === P.TRADING_VIEW;
  return (
    <div className="p-6 max-w-xl mx-auto" data-testid="not-permitted">
      <div className="term-panel rounded-sm p-6 space-y-2">
        <div className="flex items-center gap-2 text-sm font-bold"><Lock className="h-4 w-4 text-slate-400" />
          {trading ? "Trading is not enabled for your account yet" : "You do not have access to this page"}</div>
        <div className="text-xs text-slate-500">{trading
          ? "Ask an administrator to assign the TRADER role to your account."
          : "Ask an administrator if you believe you need access."}</div>
      </div>
    </div>
  );
}

// Authenticated shell. The backend is the authority on every permission; this only decides
// what to render.
function Protected({ children, permission, product, module }) {
  const { user, ready, can } = useAuth();
  const loc = useLocation();
  if (!ready) return <div className="min-h-screen flex items-center justify-center bg-[#0A0C10] text-slate-500 text-sm">Initializing terminal…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (user.password_change_required) return <ForcePasswordChange />;
  return (
    // Keyed by user id: signing in as someone else in the same tab can never reuse state.
    <AppProvider key={user.id}>
      <Layout>{permission && !can(permission) ? <NotPermitted permission={permission} />
        : <ProductGate product={product}><ModuleGate module={module}>{children}</ModuleGate></ProductGate>}</Layout>
    </AppProvider>
  );
}

// Pages of a product the account does not trade (FUTURES_ONLY / OPTIONS_ONLY / BOTH) show "Module not activated".
// Presentation only: the server refuses the product's endpoints itself.
function ProductGate({ product, children }) {
  const products = useProducts();
  if (!product || !products.loaded || allowsProduct(products, product)) return children;
  return <ModuleNotActivated />;
}

function Home() {
  const products = useProducts();
  if (!products.loaded) return null;
  return <Navigate to="/dashboard" replace />;
}

function App() {
  const { resolvedTheme } = useTheme();
  const T = P.TRADING_VIEW;
  // Development SIMULATION screens (shared mock broker) — ADMIN only; the API enforces it too.
  const S = P.SIMULATOR;
  return (
    <BrowserRouter>
      <Toaster position="bottom-right" theme={resolvedTheme || "light"} richColors closeButton />
      <AuthProvider>
        <SimulatorBar />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/account/setup" element={<AccountSetup />} />
          <Route path="/account" element={<Protected><Account /></Protected>} />
          <Route path="/admin/users" element={<Protected permission={P.USERS_READ}><AdminUsers /></Protected>} />
          <Route path="/admin/overview" element={<Protected permission={P.USERS_READ}><AdminOverview /></Protected>} />
          <Route path="/broker" element={<Protected permission={P.BROKER}><BrokerSession /></Protected>} />
          <Route path="/broker/connect/:id" element={<Protected permission={P.BROKER}><ConnectZerodha /></Protected>} />
          <Route path="/dashboard" element={<Protected permission={T}><Dashboard /></Protected>} />
          <Route path="/explorer" element={<Protected product={FUTURES} module={MODULE.FUTURES} permission={T}><Explorer /></Protected>} />
          <Route path="/opportunities" element={<Protected product={FUTURES} module={MODULE.FUTURES} permission={T}><Opportunities /></Protected>} />
          <Route path="/shadow" element={<Navigate to="/opportunities" replace />} />
          <Route path="/paper" element={<Protected product={FUTURES} module={MODULE.FUTURES} permission={T}><PaperTrading /></Protected>} />
          <Route path="/futures/builder" element={<Protected product={FUTURES} module={MODULE.FUTURES} permission={T}><FeatureUnavailable
            title="Future Builder" text="There is no separate Future Builder yet: a futures spread trade is created from a row of the Future Trade Scanner (Add Trade), which opens the trade editor."
            link="/opportunities" linkText="Open the Future Trade Scanner" /></Protected>} />
          <Route path="/trades" element={<Protected permission={T}><CommonTradePanel /></Protected>} />
          <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
          <Route path="/system-health" element={<Protected><SystemHealth /></Protected>} />
          <Route path="/active-trades" element={<Protected permission={T}><ActiveTrades /></Protected>} />
          {/* Order/Trade Book now live as Trading Book tabs; old bookmarks redirect. */}
          <Route path="/order-book" element={<Navigate to="/book?tab=orders" replace />} />
          <Route path="/trade-book" element={<Navigate to="/book?tab=tradebook" replace />} />
          <Route path="/shadow-live" element={<Protected permission={P.TRADING_LIVE}><ShadowLive /></Protected>} />
          <Route path="/book" element={<Protected permission={T}><PaperBook /></Protected>} />
          <Route path="/strategy-library" element={<Protected product={FUTURES} module={MODULE.FUTURES} permission={T}><StrategyLibrary /></Protected>} />
          <Route path="/builder" element={<Protected permission={S}><StrategyBuilder /></Protected>} />
          <Route path="/strategies" element={<Protected permission={S}><ActiveStrategies /></Protected>} />
          <Route path="/strategies/:id" element={<Protected permission={S}><StrategyDetail /></Protected>} />
          <Route path="/risk" element={<Protected permission={S}><RiskCentre /></Protected>} />
          <Route path="/journal" element={<Protected permission={S}><TradeJournal /></Protected>} />
          <Route path="/dev/legacy-dashboard" element={<Protected permission={S}><LegacyDashboard /></Protected>} />
          {/* OPTIONS ANALYSIS — read-only vertical served by the separate options worker. */}
          {OPTIONS_ENABLED && <>
            <Route path="/options" element={<Navigate to="/options/scanner" replace />} />
            <Route path="/options/scanner" element={<Protected product={OPTIONS} module={MODULE.ANALYSIS} permission={P.OPTIONS_VIEW}><OptionsScanner /></Protected>} />
            <Route path="/options/chain" element={<Protected product={OPTIONS} module={MODULE.ANALYSIS} permission={P.OPTIONS_VIEW}><OptionChain /></Protected>} />
            <Route path="/options/validation" element={<Protected product={OPTIONS} module={MODULE.ANALYSIS} permission={P.OPTIONS_VIEW}><OptionsValidation /></Protected>} />
            <Route path="/options/diagnostics" element={<Protected product={OPTIONS} module={MODULE.ANALYSIS} permission={P.OPTIONS_VIEW}><OptionsDiagnostics /></Protected>} />
            <Route path="/options/settings" element={<Protected product={OPTIONS} module={MODULE.ANALYSIS} permission={P.OPTIONS_VIEW}><OptionsSettings /></Protected>} />
            <Route path="/options/history" element={<Protected product={OPTIONS} module={MODULE.ANALYSIS} permission={P.OPTIONS_VIEW}><OptionsHistory /></Protected>} />
            {IRON_CONDOR_ENABLED && <Route path="/options/iron-condor/*" element={<Protected product={OPTIONS} module={MODULE.IRON_CONDOR} permission={P.OPTIONS_VIEW}><IronCondor /></Protected>} />}
            <Route path="/options/builder" element={<Protected product={OPTIONS} module={MODULE.HEDGE} permission={P.OPTIONS_VIEW}><FeatureUnavailable
              title="Option Builder" text="There is no separate Option Builder yet: BUY and SELL option trades are created from a row of the Option Trade Scanner (its trade ticket)."
              link="/options/scanner" linkText="Open the Option Trade Scanner" /></Protected>} />
            {OPTIONS_HEDGE_ENABLED && <Route path="/options/hedge" element={<Protected product={OPTIONS} module={MODULE.HEDGE} permission={P.OPTIONS_TRADE}><OptionsHedge /></Protected>} />}
          </>}
          <Route path="*" element={<Protected><Home /></Protected>} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
